import { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Button, Card, CircularProgress, Grid, InputLabel, Stack, TextField, Typography } from '@mui/material';
import { SearchOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import RequiredAsterisk from 'components/RequiredAsterisk';
import { fetchArticlePreview } from './api';
import { ArticlePreviewDto, ContentfulArticleSearchFieldProps, MessageArticleValidationStatus } from './dto';
import { READ_ONLY_FIELD_STYLES } from 'utils/field-styles';

const ContentfulArticleSearchField = ({
    name,
    label,
    value,
    onChange,
    onBlur,
    onValidationStatusChange,
    onArticlePreviewChange,
    error,
    helperText,
    disabled = false,
    isRequired = false,
}: ContentfulArticleSearchFieldProps) => {
    const [validationStatus, setValidationStatus] = useState<MessageArticleValidationStatus>(
        MessageArticleValidationStatus.Idle,
    );
    const [articlePreview, setArticlePreview] = useState<ArticlePreviewDto | null>(null);
    const hasAutoFetchedRef = useRef(false);

    const handleFetchPreview = useCallback(async () => {
        if (!value?.trim()) {
            return;
        }

        setValidationStatus(MessageArticleValidationStatus.Validating);
        setArticlePreview(null);
        onValidationStatusChange?.(MessageArticleValidationStatus.Validating);
        onArticlePreviewChange?.(null);

        try {
            const preview = await fetchArticlePreview(value.trim());
            setArticlePreview(preview);
            setValidationStatus(MessageArticleValidationStatus.Success);
            onValidationStatusChange?.(MessageArticleValidationStatus.Success);
            onArticlePreviewChange?.(preview);
        } catch {
            setValidationStatus(MessageArticleValidationStatus.Error);
            setArticlePreview(null);
            onValidationStatusChange?.(MessageArticleValidationStatus.Error);
            onArticlePreviewChange?.(null);
        }
    }, [value, onValidationStatusChange, onArticlePreviewChange]);

    const handleChange = (newValue: string) => {
        onChange(newValue);

        if (articlePreview || validationStatus !== MessageArticleValidationStatus.Idle) {
            setValidationStatus(MessageArticleValidationStatus.Idle);
            setArticlePreview(null);
            onValidationStatusChange?.(MessageArticleValidationStatus.Idle);
            onArticlePreviewChange?.(null);
        }
    };

    useEffect(() => {
        if (value?.trim() && !hasAutoFetchedRef.current) {
            hasAutoFetchedRef.current = true;
            handleFetchPreview().catch((error) => {
                console.error('Failed to fetch article preview: ', error);
            });
        }
    }, [handleFetchPreview, value]);

    const getValidationIcon = () => {
        if (validationStatus === MessageArticleValidationStatus.Validating) {
            return <CircularProgress size={20} />;
        }
        if (validationStatus === MessageArticleValidationStatus.Success) {
            return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '20px' }} />;
        }
        if (validationStatus === MessageArticleValidationStatus.Error) {
            return <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '20px' }} />;
        }
        return null;
    };

    return (
        <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
                <Stack spacing={2}>
                    <InputLabel htmlFor={name}>
                        {label} {isRequired && <RequiredAsterisk />}
                    </InputLabel>
                    <TextField
                        fullWidth
                        id={name}
                        name={name}
                        placeholder="Enter Article Entry ID (Contentful)"
                        value={value}
                        onChange={(e) => handleChange(e.target.value)}
                        onBlur={onBlur}
                        error={error}
                        helperText={helperText}
                        disabled={disabled}
                        InputProps={{
                            endAdornment: getValidationIcon(),
                            sx: disabled ? READ_ONLY_FIELD_STYLES : undefined,
                        }}
                    />
                    <Button
                        variant="outlined"
                        startIcon={<SearchOutlined />}
                        onClick={handleFetchPreview}
                        disabled={disabled || !value || validationStatus === MessageArticleValidationStatus.Validating}
                        fullWidth
                    >
                        Fetch Preview
                    </Button>
                </Stack>
            </Grid>

            {/* Article Preview */}
            <Grid item xs={12} md={9}>
                {articlePreview && validationStatus === MessageArticleValidationStatus.Success && (
                    <Card variant="outlined" sx={{ p: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Stack spacing={2}>
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: 'auto 1fr',
                                            gap: 2,
                                            alignItems: 'start',
                                        }}
                                    >
                                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                            Title:
                                        </Typography>
                                        <Typography variant="body1">{articlePreview.articleTitle}</Typography>

                                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                            Type:
                                        </Typography>
                                        <Typography variant="body1">
                                            {articlePreview.articleLink?.includes('inside.fifa')
                                                ? 'Cross Space Reference'
                                                : 'Article Page'}
                                        </Typography>

                                        {articlePreview.userGroups && articlePreview.userGroups.length > 0 && (
                                            <>
                                                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                                    User Groups:
                                                </Typography>
                                                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                                    {articlePreview.userGroups.map((group) => (
                                                        <Typography
                                                            key={group.id}
                                                            component="li"
                                                            variant="body1"
                                                            sx={{ mb: 0.5 }}
                                                        >
                                                            {group.name}
                                                        </Typography>
                                                    ))}
                                                </Box>
                                            </>
                                        )}
                                    </Box>
                                </Stack>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                {articlePreview.articleImage && (
                                    <img
                                        src={articlePreview.articleImage.src}
                                        alt={articlePreview.articleTitle}
                                        style={{
                                            width: '100%',
                                            aspectRatio: '16/9',
                                            objectFit: 'cover',
                                            borderRadius: '4px',
                                        }}
                                    />
                                )}
                            </Grid>
                        </Grid>
                    </Card>
                )}
            </Grid>
        </Grid>
    );
};

export default ContentfulArticleSearchField;
